import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Button,
    Checkbox,
    Text,
    Loader,
    Flex,
    ArrowLeftIcon,
    Alert,
} from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { useHistory } from "react-router-dom";
import { useSSOAuth } from "./SSOAuthService";
import PresenceSubscriptionCard, { getApiBaseUrl, parseJwt } from "./PresenceSubscriptionCard";
import DangerZoneCard from "./DangerZoneCard";

const AUTH_SCOPES = ['User.Read.All', 'Presence.Read.All', 'Calendars.Read', 'offline_access'];

const SyncSettings = () => {
    const history = useHistory();
    const { getToken } = useSSOAuth();
    const [syncOptions, setSyncOptions] = useState({
        keepDetailsUpdated: false,
        autoCreateUsers: false,
        autoDeleteUsers: false,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });
    const [authToken, setAuthToken] = useState(null);

    const debounceTimerRef = useRef(null);
    const toastTimerRef = useRef(null);
    const baseUrl = getApiBaseUrl();

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // Fetch auth token for PresenceSubscriptionCard
    useEffect(() => {
        getToken(AUTH_SCOPES).then(setAuthToken).catch(() => {});
    }, [getToken]);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ visible: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast(prev => ({ ...prev, visible: false }));
        }, 4000);
    }, []);

    // Initialize Teams SDK and fetch config
    useEffect(() => {
        const init = async () => {
            try {
                await app.initialize();
                app.notifyAppLoaded();
                app.notifySuccess();

                const token = await getToken(AUTH_SCOPES);

                const decoded = parseJwt(token);
                const tenantId = decoded ? decoded.tid : null;
                if (!tenantId) {
                    setError('Tenant-ID konnte nicht ermittelt werden.');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${baseUrl}/MSTeamsUser/syncConfig/${tenantId}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Konfiguration konnte nicht geladen werden.');
                }

                const data = await response.json();
                setSyncOptions({
                    keepDetailsUpdated: Boolean(data.keepDetailsUpdated),
                    autoCreateUsers: Boolean(data.autoCreateUsers),
                    autoDeleteUsers: Boolean(data.autoDeleteUsers),
                });
            } catch (e) {
                console.error('SyncSettings init error:', e);
                setError(e.message || 'Initialisierung fehlgeschlagen.');
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [getToken, baseUrl]);

    // Debounced save of sync options
    const saveSyncOptions = useCallback((updatedOptions) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        debounceTimerRef.current = setTimeout(async () => {
            setSaving(true);
            try {
                const token = await getToken(AUTH_SCOPES);
                const decoded = parseJwt(token);
                const tenantId = decoded ? decoded.tid : null;

                const response = await fetch(`${baseUrl}/MSTeamsUser/syncConfig`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        tenant_id: tenantId,
                        ...updatedOptions,
                    }),
                });

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || errData.message || 'Speichern fehlgeschlagen.');
                }

                showToast('success', 'Einstellungen gespeichert.');
            } catch (e) {
                console.error('Save sync options error:', e);
                showToast('error', e.message || 'Speichern fehlgeschlagen.');
            } finally {
                setSaving(false);
            }
        }, 1000);
    }, [getToken, baseUrl, showToast]);

    const handleToggle = useCallback((key) => {
        setSyncOptions(prev => {
            const updated = { ...prev, [key]: !prev[key] };
            saveSyncOptions(updated);
            return updated;
        });
    }, [saveSyncOptions]);

    // Danger zone handlers
    const handleRemoveAllMappings = useCallback(async () => {
        try {
            const token = await getToken(AUTH_SCOPES);
            const decoded = parseJwt(token);
            const tenantId = decoded ? decoded.tid : null;

            const response = await fetch(`${baseUrl}/MSTeamsUser/removeAllMappings`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ tenant_id: tenantId }),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || errData.message || 'Entfernen fehlgeschlagen.');
            }

            showToast('success', 'Alle Zuordnungen wurden entfernt.');
            // Reload the page to reflect changes
            window.location.reload();
        } catch (e) {
            console.error('Remove all mappings error:', e);
            showToast('error', e.message || 'Entfernen fehlgeschlagen.');
        }
    }, [getToken, baseUrl, showToast]);

    const handleResetEverything = useCallback(async () => {
        try {
            const token = await getToken(AUTH_SCOPES);
            const decoded = parseJwt(token);
            const tenantId = decoded ? decoded.tid : null;

            const response = await fetch(`${baseUrl}/MSTeamsUser/resetEverything`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ tenant_id: tenantId }),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || errData.message || 'Zurücksetzen fehlgeschlagen.');
            }

            showToast('success', 'Alles wurde zurückgesetzt. Weiterleitung...');
            setTimeout(() => history.push('/sync/setup'), 1500);
        } catch (e) {
            console.error('Reset everything error:', e);
            showToast('error', e.message || 'Zurücksetzen fehlgeschlagen.');
        }
    }, [getToken, baseUrl, showToast, history]);

    if (loading) {
        return (
            <Flex hAlign="center" vAlign="center" style={{ minHeight: '300px' }}>
                <Loader label="Einstellungen werden geladen..." />
            </Flex>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px 32px' }}>
                <Text error content={error} />
                <Button content="Erneut versuchen" text style={{ marginTop: '8px' }} onClick={() => window.location.reload()} />
            </div>
        );
    }

    // Keys mirror SyncSetup.jsx (onboarding). importTeamsUsers is onboarding-only
    // and intentionally excluded here.
    const toggleOptions = [
        {
            key: 'keepDetailsUpdated',
            label: 'Mitarbeiter-Details aktuell halten',
            description: 'Wenn sich Name, E-Mail oder Position in Teams ändert, wird dies automatisch in ebuero übernommen.',
        },
        {
            key: 'autoCreateUsers',
            label: 'Neue Mitarbeiter automatisch hinzufügen',
            description: 'Wenn jemand neu zu Teams hinzukommt, wird er auch in ebuero angelegt.',
        },
        {
            key: 'autoDeleteUsers',
            label: 'Ausgeschiedene Mitarbeiter automatisch entfernen',
            description: 'Wenn jemand aus Teams entfernt wird, wird er auch in ebuero entfernt (mit Vorsicht verwenden).',
        },
    ];

    return (
        <div style={{ padding: '24px 32px', maxWidth: '800px', margin: '0 auto' }}>
            {/* Back button */}
            <Button
                icon={<ArrowLeftIcon />}
                content="Zurück zum Dashboard"
                text
                onClick={() => history.push('/sync/dashboard')}
                style={{ color: '#6264A7', marginBottom: '8px', padding: '4px 0' }}
            />

            {/* Header */}
            <Text as="h2" style={{ fontSize: '24px', fontWeight: 600, color: '#1a1a2e', margin: '0 0 4px 0' }}>
                Einstellungen
            </Text>
            <Text style={{ color: '#605e5c', marginBottom: '24px', display: 'block' }}>
                Synchronisierungs- und Präsenz-Einstellungen verwalten
            </Text>

            <Flex column gap="gap.medium">
                {/* Card 1: Sync Options */}
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    borderLeft: '3px solid #6264A7',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    padding: '20px 24px',
                }}>
                    <Text as="h3" style={{ fontWeight: 600, fontSize: '16px', color: '#1a1a2e', margin: '0 0 16px 0' }}>
                        Sync-Optionen
                    </Text>

                    <Flex column gap="gap.medium">
                        {toggleOptions.map(opt => (
                            <Flex column gap="gap.smaller" key={opt.key}>
                                <Checkbox
                                    toggle
                                    label={opt.label}
                                    checked={syncOptions[opt.key]}
                                    disabled={saving}
                                    onChange={() => handleToggle(opt.key)}
                                />
                                <Text size="small" style={{ color: '#605e5c', marginLeft: '48px' }}>
                                    {opt.description}
                                </Text>
                            </Flex>
                        ))}
                    </Flex>

                    {saving && (
                        <Text size="small" style={{ color: '#6264A7', marginTop: '12px', display: 'block' }}>
                            Wird gespeichert...
                        </Text>
                    )}
                </div>

                {/* Card 2: Presence Subscription */}
                <PresenceSubscriptionCard token={authToken} />

                {/* Card 3: Danger Zone */}
                <DangerZoneCard
                    onRemoveAllMappings={handleRemoveAllMappings}
                    onResetEverything={handleResetEverything}
                    loading={saving}
                />
            </Flex>

            {/* Toast notification */}
            {toast.visible && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%',
                    transform: 'translateX(-50%)', zIndex: 100001,
                    minWidth: '300px', maxWidth: '500px',
                }}>
                    <Alert
                        content={toast.message}
                        success={toast.type === 'success'}
                        danger={toast.type === 'error'}
                        dismissible
                        onVisibleChange={(e, { visible }) => {
                            if (!visible) setToast(prev => ({ ...prev, visible: false }));
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default SyncSettings;
